import cx from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import { sha256 } from 'js-sha256'
import {
  type BaseSyntheticEvent,
  type FormEvent,
  useCallback,
  useContext,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'

import { type SanityDemoFormBlock } from '@data/sanity/queries/types/blocks'
import { AnalyticsEventName } from '@lib/analytics'
import { AnalyticsContext } from '@lib/analytics-context'
import { fadeAnimation } from '@lib/animate'
import { type DemoFormValues } from '@lib/form'
import { FormValueProvider } from '@lib/form-value-context'
import { LanguageContext } from '@lib/language-context'
import { addEmailToNewsletterList, bookADemo } from '@lib/services'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  getButtonColor,
  getButtonIconAlignment,
  getButtonSize,
  getButtonVariant,
} from '@components/buttons/button'
import Checkbox from '@components/checkbox'
import InputField from '@components/input-field'
import TelInputField from '@components/tel-input-field'
import SimplePortableText from '@components/simple-portable-text'
import ComplexPortableText from '@components/complex-portable-text'

type DemoFormProps = Omit<SanityDemoFormBlock, '_key' | '_type'> & {
  id: string
  className?: string
  isInvertedColors?: boolean
}

const DemoForm = ({
  id,
  service,
  hubSpotFormId,
  hubSpotNewsletterFormId,
  klaviyoListID,
  klaviyoNewsletterListID,
  newsletterStatement,
  terms,
  submit,
  successMsg,
  errorMsg,
  className,
  isInvertedColors,
  buttonStyle,
  analytics,
  strings: formStrings,
}: DemoFormProps) => {
  const strings = useContext(StringsContext)
  const { locale } = useContext(LanguageContext)
  const { triggerAnalyticsEvent } = useContext(AnalyticsContext)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [formValues, setFormValues] = useState<DemoFormValues>()

  const {
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<DemoFormValues>()
  const hasAcceptedTerms = watch('acceptTerms')
  const subscribeToNewsletter = watch('subscribeToNewsletter')
  register('phoneNumber', {
    required: formStrings?.phoneNumberMissing ?? strings.phoneNumberMissing,
  })
  const firstNameRegister = register('firstName', {
    required: formStrings?.firstNameMissing ?? strings.firstNameMissing,
  })
  const lastNameRegister = register('lastName', {
    required: formStrings?.lastNameMissing ?? strings.lastNameMissing,
  })
  const emailRegister = register('email', {
    required: formStrings?.emailMissing ?? strings.emailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: formStrings?.emailInvalid ?? strings.emailInvalid,
    },
  })
  const companyRegister = register('company')
  const acceptTermsRegister = register('acceptTerms')
  const subscribeToNewsletterRegister = register('subscribeToNewsletter')

  const handlePhoneChange = useCallback(
    (value: string) => {
      setValue('phoneNumber', value)
      trigger('phoneNumber')
    },
    [setValue, trigger]
  )

  const resetForm = (event: FormEvent) => {
    event.preventDefault()

    reset()
    setIsError(false)
    setIsSuccess(false)
    setIsSubmitting(false)
  }

  const onSubmit = async (
    demoFormValues: DemoFormValues,
    event?: BaseSyntheticEvent
  ) => {
    event?.preventDefault()

    const bookingTargetId =
      service === 'klaviyo'
        ? klaviyoListID
        : service === 'hubSpot'
        ? hubSpotFormId
        : null
    const newsletterTargetId =
      service === 'klaviyo'
        ? klaviyoNewsletterListID
        : service === 'hubSpot'
        ? hubSpotNewsletterFormId
        : null

    setIsError(false)
    setIsSuccess(false)
    setFormValues(demoFormValues)

    // Show an error if there is no booking target
    if (!bookingTargetId) {
      setIsError(true)
      return
    }

    // Stop if accepting of terms is required
    if (!hasAcceptedTerms && !!terms) {
      return
    }

    setIsSubmitting(true)

    try {
      if (!!subscribeToNewsletter && !!newsletterTargetId) {
        await addEmailToNewsletterList(
          locale,
          service,
          newsletterTargetId,
          !!hasAcceptedTerms,
          demoFormValues.email,
          `${demoFormValues.firstName} ${demoFormValues.lastName}`
        )
        triggerAnalyticsEvent(AnalyticsEventName.NewsletterSignUp)
      }

      await bookADemo(
        locale,
        service,
        bookingTargetId,
        !!hasAcceptedTerms,
        demoFormValues.email,
        demoFormValues.phoneNumber,
        demoFormValues.firstName,
        demoFormValues.lastName,
        demoFormValues.company
      )
      triggerAnalyticsEvent(
        analytics?.submitEventName ?? AnalyticsEventName.BookADemo,
        {
          user_data: {
            sha256_email_address: sha256(demoFormValues.email),
            phone_number: demoFormValues.phoneNumber,
            address: {
              first_name: demoFormValues.firstName,
              last_name: demoFormValues.lastName,
            },
            companyName: demoFormValues.company,
          },
        }
      )
      setIsSuccess(true)
    } catch (error) {
      console.log(error)
      setIsError(true)
    }

    setIsSubmitting(false)
  }

  if (!service) {
    return null
  }

  const isDisabled = isSubmitting || (!!terms && !hasAcceptedTerms)

  return (
    <FormValueProvider
      formValues={{
        'book-a-demo-email': formValues?.email,
        'book-a-demo-firstName': formValues?.firstName,
        'book-a-demo-lastName': formValues?.lastName,
        'book-a-demo-phoneNumber': formValues?.phoneNumber?.replace(/ /g, ''),
        'book-a-demo-company': formValues?.company,
      }}
    >
      <form className={cx(className)} onSubmit={handleSubmit(onSubmit)}>
        <AnimatePresence mode="wait">
          {!isError && !isSuccess && (
            <motion.div
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <div className="flex flex-col sm:flex-row sm:gap-4">
                <InputField
                  id={`book-a-demo-${id}-firstName`}
                  type="text"
                  formRegister={firstNameRegister}
                  placeholder={
                    formStrings?.firstNamePlaceholder ??
                    strings.firstNamePlaceholder
                  }
                  label={formStrings?.firstName ?? strings.firstName}
                  className="sm:w-1/2 mb-4"
                  errorMessage={errors.firstName?.message}
                  isInvertedColors={isInvertedColors}
                />

                <InputField
                  id={`book-a-demo-${id}-lastName`}
                  type="text"
                  formRegister={lastNameRegister}
                  placeholder={
                    formStrings?.lastNamePlaceholder ??
                    strings.lastNamePlaceholder
                  }
                  label={formStrings?.lastName ?? strings.lastName}
                  className="sm:w-1/2 mb-4"
                  errorMessage={errors.lastName?.message}
                  isInvertedColors={isInvertedColors}
                />
              </div>

              <InputField
                id={`book-a-demo-${id}-email`}
                type="email"
                formRegister={emailRegister}
                placeholder={
                  formStrings?.emailAddressPlaceholder ??
                  strings.emailAddressPlaceholder
                }
                label={formStrings?.emailAddress ?? strings.emailAddress}
                className="mb-4"
                errorMessage={errors.email?.message}
                isInvertedColors={isInvertedColors}
              />

              <TelInputField
                name="phone"
                placeholder={
                  formStrings?.phoneNumberPlaceholder ??
                  strings.phoneNumberPlaceholder
                }
                label={formStrings?.phoneNumber ?? strings.phoneNumber}
                className="mb-4"
                errorMessage={errors.phoneNumber?.message}
                isInvertedColors={isInvertedColors}
                onChange={handlePhoneChange}
              />

              <InputField
                id={`book-a-demo-${id}-company`}
                type="text"
                formRegister={companyRegister}
                placeholder={
                  formStrings?.companyPlaceholder ?? strings.companyPlaceholder
                }
                label={formStrings?.company ?? strings.company}
                className="mb-4"
                errorMessage={errors.company?.message}
                isInvertedColors={isInvertedColors}
              />

              {!!newsletterStatement &&
                (!!klaviyoNewsletterListID || !!hubSpotNewsletterFormId) && (
                  <Checkbox
                    id={`book-a-demo-${id}-subscribeToNewsletter`}
                    formRegister={subscribeToNewsletterRegister}
                    className="mb-3"
                    isInvertedColors={isInvertedColors}
                  >
                    <div className="rc rc-checkbox">
                      <SimplePortableText content={newsletterStatement} />
                    </div>
                  </Checkbox>
                )}

              {!!terms && (
                <Checkbox
                  id={`book-a-demo-${id}-acceptTerms`}
                  formRegister={acceptTermsRegister}
                  className="mb-3"
                  isInvertedColors={isInvertedColors}
                >
                  <div className="rc rc-checkbox">
                    <SimplePortableText content={terms} />
                  </div>
                </Checkbox>
              )}

              <div className="flex justify-center">
                <Button
                  type="submit"
                  className={cx('mt-3', buttonStyle?.fontCase ?? '', {
                    'w-full': buttonStyle?.isFullWidth,
                  })}
                  variant={
                    buttonStyle?.variant
                      ? getButtonVariant(buttonStyle.variant)
                      : ButtonVariant.FILLED
                  }
                  size={
                    buttonStyle?.size
                      ? getButtonSize(buttonStyle.size)
                      : ButtonSize.NORMAL
                  }
                  color={
                    buttonStyle?.color
                      ? getButtonColor(buttonStyle.color)
                      : ButtonColor.DEFAULT
                  }
                  icon={buttonStyle?.icon}
                  iconAlignment={getButtonIconAlignment(
                    buttonStyle?.iconAlignment
                  )}
                  disabled={isDisabled}
                >
                  {submit}
                </Button>
              </div>
            </motion.div>
          )}

          {isSuccess && (
            <motion.div
              key="success"
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <Alert className="rc rc-alert">
                <ComplexPortableText content={successMsg} />
              </Alert>
            </motion.div>
          )}

          {isError && (
            <motion.div
              key="error"
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <Alert
                buttonText={
                  formStrings?.buttonTryAgain ?? strings.buttonTryAgain
                }
                onClick={resetForm}
                className="rc rc-alert"
              >
                <ComplexPortableText content={errorMsg} />
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </FormValueProvider>
  )
}

export default DemoForm
